import React, { useEffect, createContext, useContext } from 'react';
import { useLocation, Route, Routes } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { CourseUIContextProvider } from '../context/useCourseContext';
import { useCourseById } from '../features/course/hooks/useCourses';
import { useUpdateCourseView } from '../features/course/hooks/useCourses';
import { Navigation, Left, Right } from '../features/course/components';

// Context to provide course data to all child components
const CourseDataContext = createContext();

export const useCourseData = () => {
  const context = useContext(CourseDataContext);
  if (!context) {
    throw new Error('useCourseData must be used within CourseDataProvider');
  }
  return context;
};

function CourseContent() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const id = queryParams.get('id');
  const { user } = useUser();

  // Fetch course data using React Query
  const { data: courseData, isLoading, isError } = useCourseById(id);
  const course = courseData?.data;

  // Update course view (add member and update user's recent courses)
  const { addMembers, updateUser } = useUpdateCourseView();

  // Update view count when course is loaded
  useEffect(() => {
    if (!course?._id || !user?.id) return;

    const updateView = async () => {
      try {
        await addMembers.mutateAsync({ courseId: course._id, userId: user.id });
        await updateUser.mutateAsync({ userId: user.id, courseId: course._id });
      } catch (error) {
        console.log('Error updating view:', error);
      }
    };

    updateView();
  }, [course?._id, user?.id]);

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p>Loading course...</p>
      </div>
    );
  }

  if (isError || !course) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-red-500">Failed to load course. Please try again.</p>
      </div>
    );
  }

  const isAdmin = user?.id === course?.userId;

  return (
    <CourseDataContext.Provider value={{ course, isAdmin, user }}>
      <div className="flex-1 flex flex-col h-full w-full overflow-hidden">
        <Navigation />
        <div className="flex-1 flex h-full w-full min-h-0">
          <Left />
          <div className="flex-1 overflow-hidden">
            <Routes>
              <Route path="handwritten" element={<Right view="handwritten" />} />
              <Route path="summary" element={<Right view="summary" />} />
              <Route path="resources" element={<Right view="resources" />} />
              <Route path="quiz" element={<Right view="quiz" />} />
              <Route path="flashcard" element={<Right view="flashcard" />} />
              <Route path="*" element={<Right />} />
            </Routes>
          </div>
        </div>
      </div>
    </CourseDataContext.Provider>
  );
}

// Wrapper component that provides UI context
function Course() {
  return (
    <CourseUIContextProvider>
      <CourseContent />
    </CourseUIContextProvider>
  );
}

export default Course;
